import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Exercice, Question, Chapitre, Lecon } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { formatDate } from '../../utils/dates';
import { logger } from '../../lib/logger';

interface ExerciceFormReadOnlyProps {
  exercice: Exercice;
}

/**
 * Version lecture seule du formulaire d'exercice
 * Affiche les données dans le même layout que ExerciceForm mais sans possibilité de modification
 */
const ExerciceFormReadOnly: React.FC<ExerciceFormReadOnlyProps> = ({ exercice }) => {
  const { environment } = useEnvironment();
  const [chapitreTitre, setChapitreTitre] = useState<string>('');
  const [leconTitre, setLeconTitre] = useState<string>('');

  useEffect(() => {
    const loadRelatedData = async () => {
      try {
        const [chapitres, lecons] = await Promise.all([
          ChapitresLeconsApiService.getChapitres(environment),
          ChapitresLeconsApiService.getLecons(environment)
        ]);

        const chapitre = chapitres.find((ch: Chapitre) => ch.id === exercice.chapitreId);
        if (chapitre) {
          setChapitreTitre(chapitre.titre);
        }

        const lecon = lecons.find((lec: Lecon) => lec.id === exercice.leconId);
        if (lecon) {
          setLeconTitre(lecon.titre);
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des données liées:', error);
      }
    };

    loadRelatedData();
  }, [exercice.chapitreId, exercice.leconId, environment]);

  const getNiveauColor = (niveau: string) => {
    switch (niveau) {
      case 'facile': return 'success';
      case 'moyen': return 'warning';
      case 'difficile': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Informations de base</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FieldDisplay label="Titre" value={exercice.titre} />
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldDisplay label="Chapitre" value={chapitreTitre || exercice.chapitreId} />
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldDisplay label="Leçon" value={leconTitre || exercice.leconId || 'N/A'} />
              </Grid>
              <Grid item xs={12}>
                <FieldDisplay label="Description" value={exercice.description} multiline />
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Niveau
                </Typography>
                <Chip
                  label={exercice.niveau}
                  color={getNiveauColor(exercice.niveau)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FieldDisplay
                  label="Durée estimée"
                  value={`${exercice.tempsEstimeMinutes || 30} minutes`}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Statut
                </Typography>
                <Chip
                  label={(exercice.statut || 'published') === 'draft' ? 'Brouillon' : 'Publié'}
                  color={(exercice.statut || 'published') === 'draft' ? 'warning' : 'success'}
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Actif
                </Typography>
                <Chip
                  icon={exercice.estActif ? <CheckCircle /> : <RadioButtonUnchecked />}
                  label={exercice.estActif ? 'Oui' : 'Non'}
                  color={exercice.estActif ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Dates */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Dates</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <FieldDisplay
                  label="Date de création"
                  value={formatDate(exercice.dateCreation, 'date')}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <FieldDisplay
                  label="Dernière modification"
                  value={formatDate(exercice.dateModification, 'date')}
                />
              </Grid>
              {exercice.datePublication && (
                <Grid item xs={6} md={4}>
                  <FieldDisplay
                    label="Date de publication"
                    value={formatDate(exercice.datePublication, 'date')}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Compétences visées */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Compétences visées ({exercice.competencesVisees.length})
            </Typography>
            {exercice.competencesVisees.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {exercice.competencesVisees.map((competence, index) => (
                  <Chip
                    key={index}
                    label={competence}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune compétence définie
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Questions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Questions ({exercice.questions.length})
            </Typography>

            {exercice.questions.map((question, index) => (
              <Accordion key={question.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Chip
                      label={`Q${index + 1}`}
                      color="primary"
                      size="small"
                    />
                    <Typography sx={{ flexGrow: 1 }}>
                      {question.texte.substring(0, 60)}{question.texte.length > 60 ? '...' : ''}
                    </Typography>
                    <Chip
                      label={`${question.points} pt${question.points > 1 ? 's' : ''}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <QuestionReadOnly question={question} />
                </AccordionDetails>
              </Accordion>
            ))}

            {exercice.questions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Aucune question définie
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * Composant pour afficher un champ en lecture seule
 */
interface FieldDisplayProps {
  label: string;
  value: string | number | undefined;
  multiline?: boolean;
}

const FieldDisplay: React.FC<FieldDisplayProps> = ({ label, value, multiline }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
        bgcolor: 'grey.50',
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        minHeight: multiline ? 60 : 'auto',
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

/**
 * Composant pour afficher une question en lecture seule
 */
interface QuestionReadOnlyProps {
  question: Question;
}

const QuestionReadOnly: React.FC<QuestionReadOnlyProps> = ({ question }) => {
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'choix_multiple': return 'Choix multiple';
      case 'vrai_faux': return 'Vrai/Faux';
      case 'texte_libre': return 'Texte libre';
      default: return type;
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FieldDisplay label="Texte de la question" value={question.texte} multiline />
      </Grid>

      <Grid item xs={6} md={4}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Type de question
        </Typography>
        <Chip label={getTypeLabel(question.type)} size="small" variant="outlined" />
      </Grid>

      <Grid item xs={3} md={2}>
        <FieldDisplay label="Points" value={question.points} />
      </Grid>

      <Grid item xs={3} md={2}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Réponse correcte
        </Typography>
        <Chip label={question.reponseCorrecte} color="success" size="small" />
      </Grid>

      {question.type === 'choix_multiple' && question.options && question.options.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Options de réponse
          </Typography>
          <Grid container spacing={1}>
            {question.options.map((option, index) => {
              const optionLetter = String.fromCharCode(65 + index);
              const isCorrect = optionLetter === question.reponseCorrecte;
              return (
                <Grid item xs={6} key={index}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: isCorrect ? 'success.50' : 'grey.50',
                      border: '1px solid',
                      borderColor: isCorrect ? 'success.main' : 'grey.200',
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{optionLetter}.</strong> {option || '(vide)'}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      )}

      {question.explication && (
        <Grid item xs={12}>
          <FieldDisplay label="Explication" value={question.explication} multiline />
        </Grid>
      )}

      {question.illustrationLatex && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Code LaTeX (Illustration)
          </Typography>
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'grey.100',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              overflow: 'auto',
              maxHeight: 200,
              whiteSpace: 'pre-wrap',
            }}
          >
            {question.illustrationLatex}
          </Box>
        </Grid>
      )}

      {question.lienLatex && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Prévisualisation de l'image LaTeX
          </Typography>
          <Box
            sx={{
              p: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 1,
              bgcolor: 'white',
            }}
          >
            <Box
              component="img"
              src={question.lienLatex}
              alt="Illustration LaTeX"
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                display: 'block',
              }}
              onError={(event) => {
                (event.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ExerciceFormReadOnly;
